import React, { useState } from 'react';
import {
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Stack,
  Box,
  Container,
  Chip,
  Tooltip
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DescriptionIcon from '@mui/icons-material/Description';
import InfoIcon from '@mui/icons-material/Info';
import {
  previewProductFreightBulk,
  processProductFreightBulk,
  downloadProductFreightBulkTemplate,
  FreightBulkPreviewResponse,
  FreightBulkPreviewRow,
  FreightBulkProcessResponse,
  FreightBulkFailedRow,
} from '../../../felles/APICalls_Freight';

const PREVIEW_ROW_LIMIT = 50;

/** Radfarge per status i forhåndsvisningen. */
const rowBackground = (status: string): object => {
  switch (status) {
    case 'Endres': return { bgcolor: '#e8f5e9' };
    case 'Feil': return { bgcolor: '#ffebee' };
    case 'Ikke funnet': return { bgcolor: '#fff8e1' };
    default: return {};
  }
};

const FreightUploadManager: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [previewData, setPreviewData] = useState<FreightBulkPreviewResponse | null>(null);
  const [uploadResult, setUploadResult] = useState<FreightBulkProcessResponse | null>(null);
  const [recipientEmail, setRecipientEmail] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  const extractErrorMessage = (err: any, fallback: string): string => {
    const message = err.response?.data?.message
      || err.response?.data?.error
      || err.message
      || fallback;
    return `${message}`;
  };

  const handleDownloadTemplate = async (): Promise<void> => {
    setError('');
    try {
      const response = await downloadProductFreightBulkTemplate();
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'fraktinfo-mal.xlsx';
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Feil ved nedlasting av mal'));
    }
  };

  // Forhåndsvisning hentes automatisk når fil velges.
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const selectedFile = e.target.files?.[0] || null;
    e.target.value = ''; // tillat å velge samme fil på nytt
    setFile(selectedFile);
    setPreviewData(null);
    setUploadResult(null);
    setError('');

    if (!selectedFile) return;

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      const response = await previewProductFreightBulk(formData);
      setPreviewData(response.data);
      if (!response.data.success && response.data.message) {
        setError(response.data.message);
      }
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Feil ved forhåndsvisning'));
    } finally {
      setLoading(false);
    }
  };

  const handleProcess = async (): Promise<void> => {
    if (!file) return;

    setLoading(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('recipientEmail', recipientEmail.trim());
      const response = await processProductFreightBulk(formData);
      if (!response.data.success && response.data.message) {
        setError(response.data.message);
        return;
      }
      setUploadResult(response.data);
      setPreviewData(null);
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Feil ved behandling av fil'));
    } finally {
      setLoading(false);
    }
  };

  const formatValue = (value: number | string | null | undefined): string =>
    value === null || value === undefined || value === '' ? '' : `${value}`;

  return (
    <Container
      maxWidth={false}
      sx={{ mt: 1, pr: 2, pl: 1, ml: 0, maxWidth: '1400px', fontFamily: 'Arial, sans-serif' }}
    >
      <Box sx={{ width: '100%' }}>
        <Paper elevation={0} sx={{ bgcolor: 'transparent' }}>
          {/* Header */}
          <Typography variant="h4" component="h1" sx={{ mb: 1, fontWeight: 400, color: '#333', paddingBottom: '10px' }}>
            Fraktinformasjon – opplasting fil
          </Typography>
          <Typography variant="body2" sx={{ color: '#666', fontSize: '16px', mb: 2, mt: 0 }}>
            Oppdater fraktinformasjon (mål, vekt, opprinnelsesland, UN-nummer, fareklasse og HS-nummer) på mange
            artikler samtidig med en Excel-fil. Filen er fasit for hele raden — tomme celler nullstiller feltet.
            Du får en forhåndsvisning før noe oppdateres.
          </Typography>

          {/* Mal + veiledning */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            sx={{ mb: 3, alignItems: { xs: 'stretch', sm: 'center' } }}
          >
            <Button
              variant="outlined"
              onClick={handleDownloadTemplate}
              startIcon={<DescriptionIcon />}
              sx={{
                textTransform: 'none',
                borderColor: '#e0e0e0',
                color: '#666666',
                '&:hover': { borderColor: '#d32f2f', backgroundColor: '#d32f2f', color: 'white' }
              }}
            >
              Last ned mal
            </Button>

            <Tooltip
              title={
                <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                  {"Programmet oppdaterer fraktinformasjon på artikler fra en Excel-fil (.xlsx).\n\nSlik gjør du:\n1. Last ned malen og fyll inn EDB-nummer per rad.\n2. VIKTIG: Filen er fasit for hele raden. Tomme celler NULLSTILLER feltet på artikkelen (0 for mål/vekt, blank for tekst) — fyll derfor ut alle verdier du vil beholde.\n3. En rad med kun EDB-nummer nullstiller alle fraktfeltene på artikkelen.\n4. Velg filen — du får automatisk en forhåndsvisning av hva som endres.\n5. Kontroller forhåndsvisningen (spesielt nullstillinger) og trykk «Oppdater artikler».\n\nRader uten reell endring hoppes over automatisk. Oppgi e-postadresse hvis du vil ha kvitteringsliste.\n\nSe også veiledningsarket i malen."}
                </Typography>
              }
              arrow
            >
              <InfoIcon style={{ cursor: 'pointer', color: '#1d6f42' }} />
            </Tooltip>
          </Stack>

          {/* Filopplasting */}
          <Paper elevation={1} sx={{ p: 2.5, mb: 2, bgcolor: '#fff', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)' }}>
            <Typography
              variant="h6"
              sx={{ mb: 1.5, display: 'flex', alignItems: 'center', color: '#555', fontSize: '16px', fontWeight: 'bold' }}
            >
              <CloudUploadIcon sx={{ mr: 1 }} />
              Last opp fil
            </Typography>
            <Stack spacing={1.5}>
              <Box>
                <Button
                  variant="contained"
                  component="label"
                  disabled={loading}
                  sx={{ bgcolor: '#d32f2f', '&:hover': { bgcolor: '#b71c1c' }, textTransform: 'none' }}
                >
                  Velg fil
                  <input type="file" accept=".xlsx" hidden onChange={handleFileChange} />
                </Button>
                {file && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    <strong>Valgt fil:</strong> {file.name}
                  </Typography>
                )}
              </Box>

              <Box>
                <Typography variant="body2" sx={{ mb: 1 }}>E-post for kvitteringsliste (valgfritt)</Typography>
                <TextField
                  type="email"
                  value={recipientEmail}
                  onChange={(e) => setRecipientEmail(e.target.value)}
                  placeholder="din@epost.no"
                  fullWidth
                  size="small"
                />
              </Box>

              <Box>
                <Button
                  onClick={handleProcess}
                  disabled={loading || !file || !previewData || !previewData.success}
                  variant="contained"
                  color="success"
                  type="button"
                  sx={{ textTransform: 'none' }}
                >
                  {loading ? 'Arbeider…' : 'Oppdater artikler'}
                </Button>
                {previewData && previewData.success && previewData.willChange === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Ingen rader medfører endringer.
                  </Typography>
                )}
              </Box>
            </Stack>

            {/* Forhåndsvisning */}
            {previewData && previewData.success && (
              <Paper elevation={1} sx={{ overflow: 'hidden', mt: 2, maxWidth: '100%' }}>
                <Box sx={{ p: 1.5, bgcolor: '#f5f5f5', borderBottom: 1, borderColor: 'divider' }}>
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                    <Typography variant="h6" sx={{ mr: 1 }}>
                      Forhåndsvisning ({previewData.totalRows} rader)
                    </Typography>
                    <Chip size="small" label={`Endres: ${previewData.willChange}`} color="success" variant="outlined" />
                    <Chip size="small" label={`Uendret: ${previewData.unchanged}`} variant="outlined" />
                    {previewData.notFound > 0 && (
                      <Chip size="small" label={`Ikke funnet: ${previewData.notFound}`} color="warning" variant="outlined" />
                    )}
                    {previewData.errorCount > 0 && (
                      <Chip size="small" label={`Feil: ${previewData.errorCount}`} color="error" variant="outlined" />
                    )}
                  </Stack>
                  {previewData.willChange > 0 && (
                    <Typography variant="body2" sx={{ mt: 1, color: '#8a6d3b' }}>
                      Tabellen viser verdiene artiklene får etter oppdateringen. Tomme celler i filen nullstiller
                      feltet — kontroller at kolonnene under viser det du forventer før du oppdaterer.
                    </Typography>
                  )}
                </Box>

                {previewData.errors && previewData.errors.length > 0 && (
                  <Box sx={{ p: 1.5, bgcolor: '#ffebee', borderBottom: 1, borderColor: 'divider' }}>
                    <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>Feil:</Typography>
                    <Box component="ul" sx={{ m: 0, pl: 2, wordBreak: 'break-word' }}>
                      {previewData.errors.map((err: string, index: number) => (
                        <Typography component="li" key={index} variant="body2" color="error">{err}</Typography>
                      ))}
                    </Box>
                  </Box>
                )}

                {previewData.rows && previewData.rows.length > 0 && (
                  <Box sx={{ maxHeight: 420, overflowY: 'auto', overflowX: 'auto' }}>
                    <Table size="small" stickyHeader sx={{ minWidth: 1060 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold' }}>Linje</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>EDB-nummer</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Endres</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }} align="right">Lengde</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }} align="right">Bredde</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }} align="right">Høyde</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }} align="right">Netto</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }} align="right">Brutto</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Land</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>UN</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Fareklasse</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>HS-nummer</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {previewData.rows.slice(0, PREVIEW_ROW_LIMIT).map((row: FreightBulkPreviewRow, index: number) => (
                          <TableRow key={index} hover sx={rowBackground(row.status)}>
                            <TableCell>{row.lineNumber}</TableCell>
                            <TableCell>{row.edbNumber}</TableCell>
                            <TableCell>{row.status}{row.message ? ` – ${row.message}` : ''}</TableCell>
                            <TableCell>{row.changedFields?.join(', ')}</TableCell>
                            <TableCell align="right">{formatValue(row.length)}</TableCell>
                            <TableCell align="right">{formatValue(row.width)}</TableCell>
                            <TableCell align="right">{formatValue(row.height)}</TableCell>
                            <TableCell align="right">{formatValue(row.netWeight)}</TableCell>
                            <TableCell align="right">{formatValue(row.grossWeight)}</TableCell>
                            <TableCell>{formatValue(row.countryOfOrigin)}</TableCell>
                            <TableCell>{formatValue(row.unNumber)}</TableCell>
                            <TableCell>{formatValue(row.dangerClass)}</TableCell>
                            <TableCell>{formatValue(row.hsNumber)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    {previewData.rows.length > PREVIEW_ROW_LIMIT && (
                      <Box sx={{ p: 1, bgcolor: '#f8f9fa', borderTop: 1, borderColor: 'divider' }}>
                        <Typography variant="body2" color="text.secondary">
                          … og {previewData.rows.length - PREVIEW_ROW_LIMIT} flere rader
                        </Typography>
                      </Box>
                    )}
                  </Box>
                )}
              </Paper>
            )}

            {/* Resultat / importrapport */}
            {uploadResult && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>Resultat</Typography>
                <Paper
                  elevation={1}
                  sx={{ p: 2, mb: 1, bgcolor: uploadResult.errors > 0 ? '#fff8e1' : '#e8f5e8' }}
                >
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    {uploadResult.errors > 0
                      ? `${uploadResult.updated} av ${uploadResult.totalRows} rader ble oppdatert — ${uploadResult.errors} rader feilet (se listen under).`
                      : `Alle rader er behandlet: ${uploadResult.updated} oppdatert, ${uploadResult.unchanged} uendret.`}
                  </Typography>
                  <Typography variant="body2"><strong>Rader i filen:</strong> {uploadResult.totalRows}</Typography>
                  <Typography variant="body2"><strong>Oppdatert:</strong> {uploadResult.updated}</Typography>
                  <Typography variant="body2"><strong>Uendret (hoppet over):</strong> {uploadResult.unchanged}</Typography>
                  <Typography variant="body2"><strong>Ikke funnet:</strong> {uploadResult.notFound}</Typography>
                  <Typography variant="body2"><strong>Feilet:</strong> {uploadResult.errors}</Typography>
                </Paper>

                {uploadResult.failedRows && uploadResult.failedRows.length > 0 && (
                  <Paper elevation={1} sx={{ overflow: 'hidden', mb: 2 }}>
                    <Box sx={{ p: 1.5, bgcolor: '#ffebee', borderBottom: 1, borderColor: 'divider' }}>
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                        Rader som feilet ({uploadResult.failedRows.length})
                      </Typography>
                    </Box>
                    <Box sx={{ maxHeight: 320, overflowY: 'auto', overflowX: 'auto' }}>
                      <Table size="small" stickyHeader sx={{ minWidth: 520 }}>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>Linje</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>EDB-nummer</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Årsak</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {uploadResult.failedRows.map((row: FreightBulkFailedRow, index: number) => (
                            <TableRow key={index} hover sx={{ bgcolor: '#ffebee' }}>
                              <TableCell>{row.lineNumber}</TableCell>
                              <TableCell>{row.edbNumber ?? ''}</TableCell>
                              <TableCell>{row.reason}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Box>
                  </Paper>
                )}

                {/* Filnivå-feil (uleselig fil o.l.) som ikke hører til én rad */}
                {(!uploadResult.failedRows || uploadResult.failedRows.length === 0)
                  && uploadResult.errorMessages && uploadResult.errorMessages.length > 0 && (
                  <Paper elevation={1} sx={{ p: 1.5, mb: 2, bgcolor: '#ffebee' }}>
                    <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>Feilmeldinger:</Typography>
                    <Box component="ul" sx={{ m: 0, pl: 2 }}>
                      {uploadResult.errorMessages.map((err: string, index: number) => (
                        <Typography component="li" key={index} variant="body2" color="error">{err}</Typography>
                      ))}
                    </Box>
                  </Paper>
                )}

                {uploadResult.emailSent && (
                  <Typography variant="body2" sx={{ color: 'green' }}>
                    ✓ Kvittering sendt til {recipientEmail}
                  </Typography>
                )}
              </Box>
            )}
          </Paper>

          {/* Feilvisning */}
          {error && (
            <Paper elevation={1} sx={{ p: 1.5, mb: 2, bgcolor: '#ffebee', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)' }}>
              <Typography variant="body2" sx={{ color: 'red' }}>{error}</Typography>
            </Paper>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default FreightUploadManager;
